import type { TextContentModifierValue } from "schemas/generated/fileModifiers";

import React, { useEffect, useRef } from "react";

import { useSelectedElement } from "@/features/react-editor/components/modifiers/store/ModifiersStoreContext";
import { parseTextFormatting } from "@/features/react-editor/components/modifiers/utils";
import IconButton from "@replo/design-system/components/button/IconButton";
import Underline from "@tiptap/extension-underline";
import { EditorContent, useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import {
  Bold,
  Italic,
  Strikethrough,
  Underline as UnderlineIcon,
} from "lucide-react";

interface ContentModifierProps {
  onChange: (modifiers: TextContentModifierValue[]) => void;
}

const ContentModifierComponent: React.FC<ContentModifierProps> = ({
  onChange,
}) => {
  const selectedElement = useSelectedElement();
  const isUpdatingRef = useRef(false);

  // Extract current value from selected element
  const currentValue = React.useMemo(() => {
    if (!selectedElement) {
      return null;
    }
    return {
      content: selectedElement.childText ?? "",
      className: selectedElement.props?.className ?? "",
    };
  }, [selectedElement]);

  const handleContentChange = (newContent: string, newClassName?: string) => {
    if (isUpdatingRef.current) {
      return;
    }

    onChange([
      {
        type: "content" as const,
        value: newContent,
        className: newClassName ?? "",
      },
    ]);
  };

  /**
   * Apply formatting to selected text, or all text if nothing is selected
   */
  const applyFormatting = (formatCommand: () => any) => {
    if (!editor) {
      return;
    }

    const { from, to } = editor.state.selection;
    const hasSelection = from !== to;

    if (hasSelection) {
      // Apply to selected text
      formatCommand();
    } else {
      // Apply to all text
      editor.chain().focus().selectAll().run();
      formatCommand();
    }
  };

  /**
   * Extract content and formatting from TipTap editor
   */
  const extractContentAndFormatting = (editor: any) => {
    // Get plain text content
    const textContent = editor.getText();

    // Use TipTap's built-in methods to check active formatting
    const formattingClasses: string[] = [];

    [
      { type: "bold", className: "font-bold" },
      { type: "italic", className: "italic" },
      { type: "underline", className: "underline" },
      { type: "strike", className: "line-through" },
    ].forEach(({ type, className }) => {
      if (editor.isActive(type)) {
        formattingClasses.push(className);
      }
    });

    const className = formattingClasses.join(" ");
    return { textContent, className };
  };

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        // Only specify the ones we want to disable
        // bold, italic, and strike are enabled by default
        heading: false,
        blockquote: false,
        bulletList: false,
        orderedList: false,
        listItem: false,
        codeBlock: false,
        horizontalRule: false,
      }),
      Underline,
    ],
    content: currentValue?.content ?? "",
    onUpdate: ({ editor }) => {
      if (isUpdatingRef.current) {
        return;
      }
      const { textContent, className } = extractContentAndFormatting(editor);
      handleContentChange(textContent, className);
    },
    editorProps: {
      attributes: {
        class: "content-modifier-editor text-sm",
      },
    },
  });

  // Update editor content and formatting when currentValue changes
  useEffect(() => {
    if (!editor || !currentValue) {
      return;
    }

    const newContent = currentValue.content ?? "";
    const newClassName = currentValue.className ?? "";
    const currentEditorContent = editor.getText();

    // Only update if content actually changed
    if (currentEditorContent !== newContent) {
      isUpdatingRef.current = true;

      // Update content
      editor.commands.setContent(newContent);

      // Apply existing formatting from className using the common utility
      const styleOverrides = selectedElement?.styleOverrides ?? {};
      const existingFormatting = parseTextFormatting(
        styleOverrides,
        newClassName,
      );

      // Clear all formatting first
      editor
        .chain()
        .selectAll()
        .unsetBold()
        .unsetItalic()
        .unsetUnderline()
        .unsetStrike()
        .run();

      // Apply existing formatting
      const chain = editor.chain().selectAll();

      if (existingFormatting.bold) {
        chain.setBold();
      }
      if (existingFormatting.italic) {
        chain.setItalic();
      }
      if (existingFormatting.underline) {
        chain.setUnderline();
      }
      if (existingFormatting.strikethrough) {
        chain.setStrike();
      }

      chain.run();

      // Reset the flag after a brief delay to allow the editor to settle
      setTimeout(() => {
        isUpdatingRef.current = false;
      }, 0);
    }
  }, [currentValue, editor, selectedElement]);

  if (!editor) {
    return null;
  }

  return (
    <div className="content-modifier">
      {/* Combined Formatting Toolbar and Editor */}
      <div className="border border-border rounded-lg overflow-hidden">
        {/* Formatting Toolbar */}
        <div className="flex gap-1 px-2 py-1 bg-surface border-b border-border items-center">
          <IconButton
            variant="tertiary"
            isActive={editor.isActive("bold")}
            size="sm"
            onClick={() =>
              applyFormatting(() => editor.chain().focus().toggleBold().run())
            }
            icon={<Bold size={14} />}
          />

          <IconButton
            variant="tertiary"
            isActive={editor.isActive("italic")}
            size="sm"
            onClick={() =>
              applyFormatting(() => editor.chain().focus().toggleItalic().run())
            }
            icon={<Italic size={14} />}
          />

          <IconButton
            variant="tertiary"
            isActive={editor.isActive("underline")}
            size="sm"
            onClick={() =>
              applyFormatting(() =>
                editor.chain().focus().toggleUnderline().run(),
              )
            }
            icon={<UnderlineIcon size={14} />}
          />

          <IconButton
            variant="tertiary"
            isActive={editor.isActive("strike")}
            size="sm"
            onClick={() =>
              applyFormatting(() => editor.chain().focus().toggleStrike().run())
            }
            icon={<Strikethrough size={14} />}
          />
        </div>

        {/* Editor Content */}
        <div
          className="p-2 min-h-[80px] bg-white cursor-text"
          onClick={() => {
            if (!editor?.isFocused) {
              editor?.commands.focus("end");
            }
          }}
        >
          <EditorContent editor={editor} />
        </div>
      </div>
    </div>
  );
};

export default ContentModifierComponent;
